import {
  RECOVERY_ESCALATE_AFTER_MS,
  SUSTAINED_STANDING_QUEUE_MS_ENTER,
  SUSTAINED_RTT_WINDOW_MS,
} from '@playcastdotio/PeerCore';
import type { SessionDowngradeFloor, StickyDowngradePolicy } from '@playcastdotio/PeerCore';
import { escalateToHard, hardEpisode, softEpisode, type RecoveryCutContext } from './RecoveryCutPolicy';
import type { RecoveryEpisodeStore } from './RecoveryEpisodeStore';

/** Why a cut, floor raise, fps-ceiling lowering or tier change may not be applied right now. */
export type DeepeningBlock = 'settling' | 'standing-buffer';

/** A for-cause floor: the rungs a cut may raise the session floor to. */
export type ForCauseFloor = Exclude<SessionDowngradeFloor, 'none'>;

export type RecoveryEntryDeps = {
  episodes: RecoveryEpisodeStore;
  cutContextFor: (peerId: string) => RecoveryCutContext;
  stickyDowngrade?: StickyDowngradePolicy;
  /** The gates: why nothing may deepen for this peer right now, or null when it may. */
  deepeningBlock: (peerId: string, bypassSettle: boolean) => DeepeningBlock | null;
  /** The floor actually rose (health event, reclaim oscillation clock). */
  onFloorRaised: (peerId: string, floor: SessionDowngradeFloor, reason: string) => void;
  debug: (tag: string, line: Record<string, unknown>) => void;
  nowMs: () => number;
};

/**
 * RecoveryEntryManager — the way INTO a recovery episode.
 *
 * Owns the three cuts (the `elevated` step, the `critical` hard cut, the soft→hard
 * escalation), the sustained-queue escalation dwell, and the for-cause sticky floor each of
 * them raises (PRODUCT DECISION D1). Every entry asks the gates first: startup is not
 * congestion and a standing buffer is not congestion, so while either gate holds the entry is
 * logged with its reason and nothing is applied.
 */
export class RecoveryEntryManager {
  constructor(private readonly deps: RecoveryEntryDeps) {}

  /**
   * A critical signal: enter the hard cut, or escalate an open episode. `bypassSettle` is for
   * loss and freeze — instantaneous, network-proven — which never wait for the settle gate.
   */
  public enterOrEscalate(peerId: string, reason: string, bypassSettle = false): void {
    const block = this.deps.deepeningBlock(peerId, bypassSettle);
    this.deps.debug('[Recovery.enter]', {
      peerId,
      reason,
      state: this.deps.episodes.stateOf(peerId),
      ...(block ? { blocked: block } : {}),
    });
    if (block) return;
    const existing = this.deps.episodes.get(peerId);
    if (!existing || existing.state === 'normal') {
      this.enterHard(peerId);
      return;
    }
    const now = this.deps.nowMs();
    if (existing.state === 'recovering' && existing.episodeCutKind === 'soft') {
      // The router owns signal dwells. Episode age is not signal duration: healthy
      // host polls and ramp steps can reset enteredAt while guest evidence persists.
      escalateToHard(existing, this.deps.cutContextFor(peerId), now);
      this.raiseStickyFloor(peerId, 'hard', 'congestion-escalated');
      // this.deps.stickyDowngrade?.lowerFpsCeiling(peerId, existing.maxFramerate);
      this.deps.episodes.apply(peerId, existing);
      return;
    }
    if (existing.state === 'recovering' && now - existing.enteredAt >= RECOVERY_ESCALATE_AFTER_MS) {
      existing.state = 'escalated';
      existing.enteredAt = now;
      existing.healthySince = null;
      this.deps.episodes.apply(peerId, existing);
      return;
    }
    // Deliberately does NOT reset `healthySince`: a repeat signal on a recovering peer used
    // to restart the exit clock, and a client reporting every 2s made the 5s hold
    // unreachable. Genuine congestion still holds the episode open through the host's own
    // poll, which clears `healthySince` in the exit gate.
  }

  /**
   * An elevated signal: the STEP cut (three quarters of the current cap, never below the hard
   * cut) with a sticky soft floor. Only *enters* an episode — a repeat elevated signal on a
   * recovering peer must not restart the exit clock, or the climb can never complete.
   */
  public enterElevated(peerId: string): void {
    const existing = this.deps.episodes.get(peerId);
    if (existing && existing.state !== 'normal') return;
    const block = this.deps.deepeningBlock(peerId, false);
    if (block) {
      this.deps.debug('[Recovery.enter]', { peerId, reason: 'congestion-elevated', state: 'normal', blocked: block });
      return;
    }
    const ctx = this.deps.cutContextFor(peerId);
    const peer = softEpisode(existing?.maxBitrateBps ?? ctx.profileMaxBitrateBps, ctx, this.deps.nowMs());
    this.raiseStickyFloor(peerId, 'soft', 'congestion-elevated');
    // this.deps.stickyDowngrade?.lowerFpsCeiling(peerId, peer.maxFramerate);
    this.deps.episodes.begin(peerId, peer);
  }

  /**
   * Elevated→hard escalation for sustained queueing. Only while in the elevated (soft) cut:
   * a queue above SUSTAINED_STANDING_QUEUE_MS_ENTER that holds for SUSTAINED_RTT_WINDOW_MS
   * escalates through the shared hard-entry path; a drop below the threshold resets the
   * dwell. Returns true when it escalated (so the caller skips the exit ramp this tick).
   */
  public considerSustainedQueueEscalation(peerId: string, standingQueueMs: number): boolean {
    const peer = this.deps.episodes.get(peerId);
    if (!peer || peer.state === 'normal' || peer.episodeCutKind !== 'soft') return false;
    if (standingQueueMs < SUSTAINED_STANDING_QUEUE_MS_ENTER) {
      peer.sustainedQueueSince = null;
      return false;
    }
    const now = this.deps.nowMs();
    if (peer.sustainedQueueSince === null) {
      peer.sustainedQueueSince = now;
      return false;
    }
    if (now - peer.sustainedQueueSince < SUSTAINED_RTT_WINDOW_MS) return false;
    peer.sustainedQueueSince = null;
    this.enterOrEscalate(peerId, 'sustained-standing-queue');
    return true;
  }

  /**
   * PRODUCT DECISION D1 — raise the peer's session floor for cause. The floor is sticky: it
   * lowers only through the reclaim manager's evidenced release. Reports the rise to the
   * owner when the floor actually moved; a blip that recovers inside its window never gets here.
   */
  public raiseStickyFloor(peerId: string, floor: ForCauseFloor, reason: string): void {
    const policy = this.deps.stickyDowngrade;
    if (!policy) return;
    const before = policy.floorFor(peerId);
    const next = policy.raise(peerId, floor, reason);
    if (next.floor !== before) this.deps.onFloorRaised(peerId, next.floor, reason);
  }

  private enterHard(peerId: string): void {
    const peer = hardEpisode(this.deps.cutContextFor(peerId), this.deps.nowMs());
    this.raiseStickyFloor(peerId, 'hard', 'congestion-critical');
    // this.deps.stickyDowngrade?.lowerFpsCeiling(peerId, peer.maxFramerate);
    this.deps.episodes.begin(peerId, peer);
  }
}
